import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { NoRole } from './Guards';
import { AssistantDrawer } from './AssistantDrawer';
import { useAssistantEnabled } from '@/lib/assistant';

const NAV = [
  { to: '/', label: 'Home', icon: '⌂', end: true },
  { to: '/bills', label: 'Bills', icon: '▤', roles: ['viewer', 'accountant', 'approver'] },
  { to: '/expenses', label: 'Expenses', icon: '✎' },
  { to: '/payments', label: 'Payments', icon: '₹' },
  { to: '/sales', label: 'Sales', icon: '⇡' },
  { to: '/bank', label: 'Bank', icon: '⌸' },
  { to: '/cash', label: 'Cash', icon: '⌼' },
  { to: '/approvals', label: 'Approvals', icon: '✓', badge: 'approvals' },
  { to: '/parties', label: 'Parties', icon: '☺' },
  { to: '/journal', label: 'Books', icon: '≡' },
  { to: '/reports', label: 'Reports', icon: '◫' },
  { to: '/tax', label: 'Tax', icon: '§' },
  { to: '/partners', label: 'Partners', icon: '⚭' },
  { to: '/close', label: 'Month end', icon: '⏎' },
  { to: '/assistant', label: 'Assistant', icon: '✦', ai: true },
  { to: '/settings', label: 'Settings', icon: '⚙', admin: true },
];

// Each address gets a fresh page (scroll and form state reset), except the assistant: a first message gives its conversation
// an address mid-turn, and rebuilding the page then would drop the answer that is streaming. The page resets its own panel.
const pageKey = (path: string) => (path === '/assistant' || path.startsWith('/assistant/c/') ? '/assistant' : path);

export function Layout() {
  const { me, roles, settings, signOut, hasRole } = useAuth();
  const aiOn = useAssistantEnabled();
  const loc = useLocation();
  const dash = useQuery({ queryKey: ['dashboard-badges'], queryFn: () => api.dashboard(), staleTime: 30_000, enabled: roles.length > 0 });
  if (roles.length === 0) return <NoRole />;
  const needs = (dash.data?.needs_you ?? {}) as Record<string, number>;
  const firm = settings['firm.trade_name'] ?? settings['firm.legal_name'] ?? 'Books';
  const items = NAV.filter((n) => (!n.admin || hasRole('admin')) && (!n.ai || aiOn));
  return (
    <div className="shell">
      <nav className="rail" aria-label="Main">
        <div className="brand"><span className="brand-mark">बही</span><span className="brand-name">{firm}</span></div>
        <ul>
          {items.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'active' : '')}>
                <span className="icon" aria-hidden>{n.icon}</span><span>{n.label}</span>
                {n.badge && needs[n.badge] > 0 && <span className="pill">{needs[n.badge]}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="rail-foot">
          <div className="who" title={me?.email}>{me?.email}<br /><small>{roles.join(', ')}</small></div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={signOut}>Sign out</button>
        </div>
      </nav>
      <main className="main" key={pageKey(loc.pathname)}>
        <Outlet />
      </main>
      <AssistantDrawer />
      <nav className="tabsbar" aria-label="Main (mobile)">
        {[items[0], items[2], items[3], items[1], items[7]].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'active' : '')}><span className="icon" aria-hidden>{n.icon}</span><span>{n.label}</span></NavLink>
        ))}
        <NavLink to="/more"><span className="icon" aria-hidden>⋯</span><span>More</span></NavLink>
      </nav>
    </div>
  );
}

export function More() {
  const { hasRole } = useAuth();
  const aiOn = useAssistantEnabled();
  return (
    <div className="page">
      <h1>More</h1>
      <ul className="more-list">
        {NAV.filter((n) => (!n.admin || hasRole('admin')) && (!n.ai || aiOn)).map((n) => <li key={n.to}><NavLink to={n.to} end={n.end}><span className="icon" aria-hidden>{n.icon}</span>{n.label}</NavLink></li>)}
      </ul>
    </div>
  );
}
